import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import IconButton from "@material-ui/core/IconButton";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch } from "react-redux";
import { setAnalyticsDetails } from "../../../features/layers/overlaylayerslice";
import axios from 'axios';

// Helper function to get WBGT recommendation based on the uploaded image criteria
const getWBGTRecommendation = (value) => {
  if (value === null || value === undefined) return "No data";
  const val = parseFloat(value);
  
  if (val < 23) return "No recommendation";
  if (val >= 23 && val < 25) return "Very heavy work (45/15)";
  if (val >= 25 && val < 28) return "Heavy work (30/15)";
  if (val >= 28 && val < 30) return "Moderate work (30/30)";
  if (val >= 30 && val <= 33) return "Light work (15/45)";
  if (val > 33) return "Rest";
  
  return "Unknown";
};

const TimelineWgbt = ({ data, fulldates }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const intervalTime = 5000;

  const API_BASE_URL = "https://api.ncmrwf.gov.in/uploads/";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch available dates from API
  useEffect(() => {
    const fetchDates = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const currentDate = fulldates[0];
        
        const response = await axios.get(API_BASE_URL, {
          params: {
            data_type: 'wbgt',
            date: currentDate
          }
        });

        if (response.data && response.data.records && response.data.records.length > 0) {
          const records = response.data.records;
          
          // Map to group by date and track times + WBGT values
          const dateMap = new Map();
          
          records.forEach(record => {
            const date = record.date;
            const time = record.time.substring(0, 5);
            // Assuming the API returns the value as 'value' or 'wbgt'
            const value = record.value !== undefined ? record.value : record.wbgt; 
            
            if (!dateMap.has(date)) {
              dateMap.set(date, new Map());
            }
            
            // Map time string to its WBGT value
            dateMap.get(date).set(time, value);
          });
          
          const dateArray = Array.from(dateMap.entries()).map(([date, timeMap]) => {
            // Sort times
            const times = Array.from(timeMap.keys()).sort((a, b) => {
              const [hourA, minuteA] = a.split(':').map(Number);
              const [hourB, minuteB] = b.split(':').map(Number);
              
              if (hourA !== hourB) return hourA - hourB;
              return minuteA - minuteB;
            }).map(time => ({
              time,
              value: timeMap.get(time) // Attach the WBGT value
            }));
            
            return { date, times };
          });
          
          dateArray.sort((a, b) => new Date(a.date) - new Date(b.date));
          setDates(dateArray);
        } else {
          setError('No data available for the selected date');
        }
      } catch (err) {
        console.error('Error fetching dates:', err);
        setError('Failed to load timeline data');
      } finally {
        setLoading(false);
      }
    };

    fetchDates();
  }, [fulldates]);

  const generateSteps = () => {
    if (dates.length === 0) return [];

    const steps = [];
    dates.forEach(dateObj => {
      dateObj.times.forEach(timeObj => {
        steps.push({
          date: dateObj.date,
          time: timeObj.time,
          value: timeObj.value,
          label: timeObj.time === "00:00" ? dateObj.date : "" 
        });
      });
    });
    return steps;
  };

  const steps = generateSteps();

  const togglePlayPause = () => {
    if (steps.length === 0) return;
    setIsPlaying((prevState) => !prevState);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && steps.length > 0) {
      if (currentStep < steps.length - 1) {
        interval = setInterval(() => {
          setCurrentStep((prevStep) => {
            const nextStep = prevStep + 1;
            if (nextStep >= steps.length) {
              setIsPlaying(false);
              return prevStep;
            }
            dispatch(
              setAnalyticsDetails({
                id: data.id - 1,
                date: formatDateTime(steps[nextStep].date, steps[nextStep].time),
                isPlaying: true,
              })
            );
            return nextStep;
          });
        }, intervalTime);
      } else {
        setCurrentStep(0);
        dispatch(
          setAnalyticsDetails({
            id: data.id - 1,
            date: formatDateTime(steps[0].date, steps[0].time),
            isPlaying: true,
          })
        );
      }
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, steps.length, dispatch, data.id, steps]);

  const formatDateTime = (date, time) => {
    const [year, month, day] = date.split('-');
    const [hour, minute] = time.split(':');
    return `${year}-${month}-${day}-hr-${hour}-${minute}-00`;
  };

  // Convert UTC time to IST strictly by adding exactly 5 hours and 30 minutes (+5:30)
  const convertUTCtoIST = (utcTime) => {
    const [hours, minutes] = utcTime.split(':').map(Number);
    
    let istHours = hours + 5;
    let istMinutes = minutes + 30;
    
    // Handle minute overflow (adds exactly 30 minutes, rolls over hour if necessary)
    if (istMinutes >= 60) {
      istHours += 1;
      istMinutes -= 60;
    }
    
    // Handle hour overflow
    if (istHours >= 24) {
      istHours -= 24;
    }
    
    return `${istHours.toString().padStart(2, '0')}:${istMinutes.toString().padStart(2, '0')}`;
  };

  const handleStepChange = (e) => {
    if (!sliderRef.current || steps.length === 0) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const newStep = Math.round(percent * (steps.length - 1));
    
    setCurrentStep(newStep);
    setIsPlaying(false);
    dispatch(setAnalyticsDetails({ 
      id: data.id - 1, 
      date: formatDateTime(steps[newStep].date, steps[newStep].time),
      isPlaying: false, 
    }));
  };

  const handleMouseMove = (e) => {
    if (!sliderRef.current || steps.length === 0) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const step = Math.round(percent * (steps.length - 1));
    
    setHoveredStep(step);
  };

  if (loading) {
    return (
      <TimelineWrapper ref={containerRef}>
        <ControlsContainer>
          <LoadingText>Loading timeline data...</LoadingText>
        </ControlsContainer>
      </TimelineWrapper>
    );
  }

  if (error && steps.length === 0) {
    return (
      <TimelineWrapper ref={containerRef}>
        <ControlsContainer>
          <ErrorText>{error}</ErrorText>
        </ControlsContainer>
      </TimelineWrapper>
    );
  }

  if (steps.length === 0) {
    return (
      <TimelineWrapper ref={containerRef}>
        <ControlsContainer>
          <EmptyText>No timeline data available</EmptyText>
        </ControlsContainer>
      </TimelineWrapper>
    );
  }

  return (
    <TimelineWrapper ref={containerRef}>
      <ControlsContainer>
        <PlayPauseButton 
          $isMobile={isMobile}
          onClick={togglePlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
          disabled={steps.length === 0}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </PlayPauseButton>
        
        <SliderContainer $isMobile={isMobile}>
          <InfoText $isMobile={isMobile}>
            {steps.length} time steps available
          </InfoText>
          
          <SliderTrack 
            ref={sliderRef}
            onClick={handleStepChange}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredStep(null)}
            $isMobile={isMobile}
            $disabled={steps.length === 0}
          >
            <SliderProgress style={{ 
              width: steps.length > 0 ? `${(currentStep / (steps.length - 1)) * 100}%` : '0%' 
            }} />
            
            {!isMobile && steps.length > 0 && (
              <Tooltip
                open={true}
                title={
                  <TooltipContent>
                    <div><strong>Date:</strong> {steps[currentStep].date}</div>
                    <div><strong>Time:</strong> {steps[currentStep].time} UTC | {convertUTCtoIST(steps[currentStep].time)} IST</div>
                    {steps[currentStep].value !== undefined && (
                      <>
                        <Divider />
                        <div><strong>WBGT:</strong> {steps[currentStep].value}°C</div>
                        <div><strong>Action:</strong> {getWBGTRecommendation(steps[currentStep].value)}</div>
                      </>
                    )}
                  </TooltipContent>
                }
                placement="top"
                arrow
              >
                <SliderThumb 
                  style={{ left: `${(currentStep / (steps.length - 1)) * 100}%` }} 
                  $isMobile={isMobile}
                />
              </Tooltip>
            )}
            
            {steps.map((step, index) => {
              const shouldShowMark = !isMobile || index % 3 === 0;
              
              return (
                <React.Fragment key={index}>
                  {shouldShowMark && (
                    <>
                      <SliderMark 
                        style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                        onMouseEnter={() => setHoveredStep(index)}
                        $isMobile={isMobile}
                      />
                      <TimeLabel 
                        style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                        $isMobile={isMobile}
                      >
                        <div>{step.time} UTC</div>
                        <div>{convertUTCtoIST(step.time)} IST</div>
                      </TimeLabel>
                    </>
                  )}
                  
                  {step.label && (
                    <DateLabel 
                      style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                      $isMobile={isMobile}
                    >
                      {isMobile ? formatMobileDate(step.date) : step.date}
                    </DateLabel>
                  )}
                </React.Fragment>
              );
            })}
            
            {!isMobile && hoveredStep !== null && hoveredStep !== currentStep && steps.length > 0 && (
              <Tooltip
                open={true}
                title={
                  <TooltipContent>
                    <div><strong>Date:</strong> {steps[hoveredStep].date}</div>
                    <div><strong>Time:</strong> {steps[hoveredStep].time} UTC | {convertUTCtoIST(steps[hoveredStep].time)} IST</div>
                    {steps[hoveredStep].value !== undefined && (
                      <>
                        <Divider />
                        <div><strong>WBGT:</strong> {steps[hoveredStep].value}°C</div>
                        <div><strong>Action:</strong> {getWBGTRecommendation(steps[hoveredStep].value)}</div>
                      </>
                    )}
                  </TooltipContent>
                }
                placement="top"
                arrow
              >
                <HoverIndicator style={{ left: `${(hoveredStep / (steps.length - 1)) * 100}%` }} />
              </Tooltip>
            )}
          </SliderTrack>
          
          {isMobile && steps.length > 0 && (
            <MobileTimeDisplay>
              <TimeInfo>
                <div>Date: {steps[currentStep].date}</div>
                <TimeRow>
                  <span>UTC: {steps[currentStep].time}</span>
                  <span>IST: {convertUTCtoIST(steps[currentStep].time)}</span>
                </TimeRow>
                {steps[currentStep].value !== undefined && (
                  <RecommendationRow>
                    WBGT: {steps[currentStep].value}°C - {getWBGTRecommendation(steps[currentStep].value)}
                  </RecommendationRow>
                )}
              </TimeInfo>
              <StepCounter>
                {currentStep + 1} / {steps.length}
              </StepCounter>
            </MobileTimeDisplay>
          )}
        </SliderContainer>
      </ControlsContainer>
    </TimelineWrapper>
  );
};

const formatMobileDate = (date) => {
  const [year, month, day] = date.split('-');
  return `${month}/${day}`;
};

export default TimelineWgbt;

// --- STYLED COMPONENTS (Keep your existing ones, plus these minor updates) ---

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.3);
  margin: 2px 0;
`;

const RecommendationRow = styled.div`
  font-size: 11px;
  color: #ffb74d;
  margin-top: 2px;
  font-weight: 600;
`;

const TimelineWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  color: white;
  border-radius: 8px;
  position: fixed;
  z-index: 999;
  bottom: 30px;
  left: 10px;
  right: 10px;
  background: rgba(3, 57, 105, 0.66);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: calc(100% - 20px);
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (min-width: 480px) {
    padding: 10px 15px;
    bottom: 15px;
  }

  @media (min-width: 768px) {
    left: 20px;
    right: 20px;
    bottom: 20px;
    max-width: calc(100% - 40px);
    padding: 12px 20px;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;

  @media (min-width: 480px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    gap: 25px;
  }
`;

const PlayPauseButton = styled(IconButton)`
  color: #ff5722;
  width: ${props => props.$isMobile ? '32px' : '40px'};
  height: ${props => props.$isMobile ? '32px' : '40px'};
  background-color: white !important;
  border-radius: 50%;
  flex-shrink: 0;
  padding: 0;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    background-color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  & svg {
    font-size: ${props => props.$isMobile ? '20px' : '24px'};
    transition: font-size 0.2s ease;
  }
`;

const SliderContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding: ${props => props.$isMobile ? '5px 0' : '10px 0'};
  position: relative;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$isMobile ? '3px' : '4px'};
  background: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 2px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  margin-top: ${props => props.$isMobile ? '25px' : '35px'};
`;

const SliderProgress = styled.div`
  position: absolute;
  height: 100%;
  background: #ff5722;
  border-radius: 2px;
  transition: width 0.1s ease;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '12px' : '14px'};
  height: ${props => props.$isMobile ? '12px' : '14px'};
  background: #ff5722;
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
`;

const SliderMark = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '4px' : '6px'};
  height: ${props => props.$isMobile ? '4px' : '6px'};
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 1;
`;

const TimeLabel = styled.div`
  position: absolute;
  top: -25px;
  transform: translateX(-50%);
  font-size: 9px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  text-align: center;
  z-index: 1;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 4px;
  border-radius: 3px;
  min-width: 50px;
  line-height: 1.2;
  
  div:first-child {
    font-weight: bold;
    color: #4fc3f7;
  }
  
  div:last-child {
    font-size: 8px;
    color: #69f0ae;
  }
  
  @media (min-width: 480px) {
    font-size: 10px;
    min-width: 60px;
    top: -30px;
    
    div:last-child {
      font-size: 9px;
    }
  }
  
  @media (min-width: 768px) {
    top: -35px;
  }
`;

const DateLabel = styled.div`
  position: absolute;
  top: -45px;
  transform: translateX(-50%);
  font-size: ${props => props.$isMobile ? '9px' : '10px'};
  color: white;
  white-space: nowrap;
  font-weight: bold;
  z-index: 1;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 3px;

  @media (min-width: 480px) {
    font-size: ${props => props.$isMobile ? '10px' : '11px'};
    top: ${props => props.$isMobile ? '-50px' : '-55px'};
  }
`;

const HoverIndicator = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`;

const MobileTimeDisplay = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  padding: 6px 10px;
  border-radius: 4px;
`;

const TimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const TimeRow = styled.div`
  display: flex;
  gap: 15px;
  font-size: 11px;
  
  span:first-child {
    color: #4fc3f7;
  }
  
  span:last-child {
    color: #69f0ae;
  }
`;

const StepCounter = styled.span`
  font-size: 11px;
  opacity: 0.8;
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
`;

const InfoText = styled.div`
  position: absolute;
  top: -30px;
  left: 0;
  font-size: ${props => props.$isMobile ? '10px' : '11px'};
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: white;
  text-align: center;
  width: 100%;
  padding: 10px;
`;

const ErrorText = styled.div`
  font-size: 14px;
  color: #ff6b6b;
  text-align: center;
  width: 100%;
  padding: 10px;
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  width: 100%;
  padding: 10px;
`;